const MAX_PATCH_OPERATIONS = 80
const UNSAFE_SEGMENTS = new Set(['__proto__', 'prototype', 'constructor'])

function sameScalar(left, right) {
  return Object.is(left, right)
}

function sameValue(left, right) {
  if (sameScalar(left, right)) return true
  if (
    !left
    || !right
    || typeof left !== 'object'
    || typeof right !== 'object'
    || Array.isArray(left) !== Array.isArray(right)
  ) return false

  if (Array.isArray(left)) {
    return left.length === right.length
      && left.every((value, index) => sameValue(value, right[index]))
  }

  const leftKeys = Object.keys(left)
  const rightKeys = Object.keys(right)
  return leftKeys.length === rightKeys.length
    && leftKeys.every((key) => (
      Object.prototype.hasOwnProperty.call(right, key)
      && sameValue(left[key], right[key])
    ))
}

function slidesAreStructurallyCompatible(previous, next) {
  if (!Array.isArray(previous) || !Array.isArray(next) || previous.length !== next.length) {
    return false
  }
  return previous.every((slide, index) => (
    String(slide?.id || '') === String(next[index]?.id || '')
  ))
}

function canPatchPath(path) {
  return path.every((segment) => (
    segment !== ''
    && !UNSAFE_SEGMENTS.has(segment)
    && /^[A-Za-z0-9_-]+$/.test(segment)
  ))
}

function collect(previous, next, path, operations) {
  if (sameScalar(previous, next)) return
  if (!path.length && (!previous || !next)) {
    operations.push({ full: true })
    return
  }

  const pathString = path.join('.')
  if (
    pathString === 'schemaVersion'
    || pathString === 'template'
    || pathString.startsWith('template.')
  ) {
    if (!sameValue(previous, next)) operations.push({ full: true })
    return
  }

  if (pathString === 'slides' && !slidesAreStructurallyCompatible(previous, next)) {
    operations.push({ full: true })
    return
  }

  if (Array.isArray(previous) || Array.isArray(next)) {
    if (pathString === 'slides') {
      next.forEach((slide, index) => collect(previous[index], slide, [...path, String(index)], operations))
    } else if (!sameValue(previous, next)) {
      operations.push({ op: 'set', path: pathString, value: next })
    }
    return
  }

  const previousIsObject = previous && typeof previous === 'object'
  const nextIsObject = next && typeof next === 'object'
  if (!previousIsObject || !nextIsObject) {
    if (canPatchPath(path)) operations.push({ op: 'set', path: pathString, value: next })
    else operations.push({ full: true })
    return
  }

  const previousKeys = Object.keys(previous)
  const nextKeys = Object.keys(next)
  const keysChanged = previousKeys.length !== nextKeys.length
    || previousKeys.some((key) => !Object.prototype.hasOwnProperty.call(next, key))

  // Asset URL maps commonly gain or lose opaque UUID keys. Sending the map once
  // is both safer and much smaller than repeating it with every text patch.
  if (keysChanged && pathString === 'assetUrls') {
    operations.push({ op: 'set', path: pathString, value: next })
    return
  }
  if (keysChanged) {
    if (path.length && canPatchPath(path)) {
      operations.push({ op: 'set', path: pathString, value: next })
    } else {
      operations.push({ full: true })
    }
    return
  }

  nextKeys.forEach((key) => collect(previous[key], next[key], [...path, key], operations))
}

export function diffPreviewConfiguration(previous, next) {
  if (previous === next) return { kind: 'patch', operations: [] }
  const operations = []
  collect(previous, next, [], operations)
  if (
    operations.some((operation) => operation.full)
    || operations.length > MAX_PATCH_OPERATIONS
  ) {
    return { kind: 'full', operations: [] }
  }
  return { kind: 'patch', operations }
}

export function mergePreviewPatches(existing, incoming, configuration) {
  if (!existing || incoming.kind === 'full') {
    return {
      kind: incoming.kind,
      configuration,
      operations: incoming.kind === 'patch' ? incoming.operations : [],
    }
  }
  if (existing.kind === 'full') {
    return { ...existing, configuration }
  }

  const byPath = new Map(existing.operations.map((operation) => [operation.path, operation]))
  incoming.operations.forEach((operation) => byPath.set(operation.path, operation))
  const operations = [...byPath.values()]
  return {
    kind: operations.length > MAX_PATCH_OPERATIONS ? 'full' : 'patch',
    configuration,
    operations: operations.length > MAX_PATCH_OPERATIONS ? [] : operations,
  }
}
